import {Component, OnDestroy, OnInit} from '@angular/core';
import {PageGlobalParameters} from '@models/pages/page-global-parameters';
import {PagesHelperService} from '@services/pages/pages-helper.service';
import {ActivatedRoute} from '@angular/router';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {Machine} from '@models/machine/machine';
import {MachineService} from '@services/administration/machine/machine.service';
import {OrganizationService} from '@services/administration/organization/organization.service';
import {Organization} from '@models/organization/organization';
import {TableauPageConfigurationService} from '@services/pages/tableau-page/tableau-page-configuration.service';
import {TableauPage} from '@models/sidebar/response/tableau-page';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {concatMap, takeUntil} from 'rxjs/operators';
import {forkJoin, Observable, Subject} from 'rxjs';
import {MenuItem} from '@models/sidebar/menu-item';
import {BreadcrumbService} from '@services/breadcrumb/breadcrumb.service';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';
import {User} from '@models/user/user';
import {Role} from '@models/user/role';

@Component({
    selector: 'scitis-tableau-page-container',
    templateUrl: './tableau-page-container.component.html',
    styleUrls: ['./tableau-page-container.component.scss'],
    standalone: false
})
export class TableauPageContainerComponent implements OnInit, OnDestroy {

  public pageGlobalParameters: PageGlobalParameters = new PageGlobalParameters();
  public machine: Machine = new Machine();
  public organization: Organization = new Organization();
  public tableauPageConfiguration: TableauPage = new TableauPage('', '', false);
  public breadcrumbRoutes: MenuItem[] = [];
  public dataIsLoaded = false;
  public isAdmin = false;
  public isLowerThanAdmin = false;
  private $destroy: Subject<boolean> = new Subject<boolean>();

  constructor(private activatedRoute: ActivatedRoute,
              private firebaseAuthenticationService: FirebaseAuthenticationService,
              private pagesHelperService: PagesHelperService,
              private organizationService: OrganizationService,
              private machineService: MachineService,
              private tableauPageConfigurationService: TableauPageConfigurationService,
              private loadingIndicator: LoadingIndicatorService,
              private breadcrumbService: BreadcrumbService) {
  }

  public get machineOrGroupId() {
    let id: string;
    if (this.isUnderMachine()) {
      id = this.pageGlobalParameters.machineId;
    } else {
      id = this.pageGlobalParameters.machineryGroupId;
    }
    return id;
  }

  ngOnInit(): void {
    this.loadingIndicator.show();
    this.pageGlobalParameters = this.pagesHelperService.getPageDetailsFromUrl(this.activatedRoute.snapshot.paramMap);
    this.generateRoutes(this.pageGlobalParameters.associatedMenuItem);
    const viewDetails = this.getViewDetails();
    const details = this.getMachineAndOrganizationDetails();
    this.getUserRole();

    forkJoin([details, viewDetails]).pipe(takeUntil(this.$destroy)).subscribe(
        () => {
          this.loadingIndicator.hide();
          this.dataIsLoaded = true;
        }
    );
  }

  ngOnDestroy(): void {
    this.$destroy.next(true);
    this.$destroy.complete();
  }

  onTableauPageConfigurationChanged($event: TableauPage) {
    this.tableauPageConfiguration = {...$event};
  }

  private getViewDetails(): Observable<TableauPage> {
    const id = this.machineOrGroupId;

    const viewObservable = this.tableauPageConfigurationService.getTableauViewById(id, this.pageGlobalParameters.pageId, !this.isUnderMachine());

    viewObservable.subscribe({
      next: (view) => {
        this.tableauPageConfiguration = view;
      }, 
      error: (error) => {
        console.log(error);
      }
    });

    return viewObservable;
  }

  private isUnderMachine() {
    return this.pageGlobalParameters.parentType === MenuItemType.MACHINE;
  }

  private getMachineAndOrganizationDetails() {
    if (!this.pageGlobalParameters.machineId || !this.isUnderMachine()) {
      return this.getUserOrganizationDetails();
    } else {
      return this.getMachineOrganizationDetails();
    }
  }

  private getMachineOrganizationDetails() {
    const machineDetailsSubscription = this.machineService.getMachineWithoutSignals(this.pageGlobalParameters.machineId);

    machineDetailsSubscription.pipe(
      takeUntil(this.$destroy),
        concatMap(
            machine => {
              this.machine = machine;
              return this.getOrganizationDetails(machine.parentId);
            }
        )
    ).subscribe();
    return machineDetailsSubscription;
  }

  private getUserOrganizationDetails() {
    const userDetailsSubscription = this.firebaseAuthenticationService.getLoggedUserDetails();
    userDetailsSubscription.pipe(
      takeUntil(this.$destroy),
        concatMap(
            user => this.getOrganizationDetails(user.parentId)
        )
    ).subscribe();
    return userDetailsSubscription;
  }

  private getUserRole() {
    this.firebaseAuthenticationService.getLoggedUserDetails().pipe(
      takeUntil(this.$destroy),
    ).subscribe({
      next: (user: User) => {
        const userRole: Role = new Role(user);
        this.isAdmin = userRole.isAdminRole;
        this.isLowerThanAdmin = !userRole.isAdminRole && !userRole.isTechAdminRole && !userRole.isSuperAdminRole;
      }
    });
  }


  private getOrganizationDetails(organizationId) {
    const organizationObservable = this.organizationService.getOrganizationDetails(organizationId);
    organizationObservable.pipe(takeUntil(this.$destroy)).subscribe({
      next: (organization: Organization) => {
        this.organization = organization;
      },
      error:  (error) => {
        console.log(error);
      }
    });

    return organizationObservable;
  }

  private generateRoutes(menuItem: MenuItem) {
    this.breadcrumbRoutes = this.breadcrumbService.generateRoutes(menuItem);
  }
}
